import type { GridColDef } from '@mui/x-data-grid-pro';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  IconButton,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { DataGridPro } from '@mui/x-data-grid-pro';
import { Edit, OpenInNew } from '@mui/icons-material';
import dayjs from 'dayjs';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { useApi } from '@shared/services/api';
import AdminAccountDetailPanel from './AdminAccountDetailPanel';

interface PlanFeatures {
  [key: string]: unknown;
}

interface AdminAccountRow {
  id: number;
  name: string;
  createdAt: string;
  ownerEmail: string | null;
  stripeCustomerId: string | null;
  stripeSubscriptionId: string | null;
  subscription: null | {
    id: number;
    planSlug: string;
    planName: string | null;
    status: string;
    term: string;
    startDate: string;
    endDate: string | null;
    featureOverrides: PlanFeatures | null;
    planFeatures: PlanFeatures;
    effectiveFeatures: PlanFeatures;
  };
}

function formatDate(date: string | null | undefined): string {
  return date ? dayjs(date).format('YYYY-MM-DD') : '-';
}

function stripeCustomerUrl(stripeCustomerId: string | null): string | null {
  if (!stripeCustomerId) return null;
  return `https://dashboard.stripe.com/customers/${stripeCustomerId}`;
}

function AdminAccountTable(): React.ReactElement {
  const api = useApi();
  const [rows, setRows] = useState<AdminAccountRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [openAccount, setOpenAccount] = useState<AdminAccountRow | null>(null);

  const apiRef = useRef(api);
  apiRef.current = api;

  const [searchInput, setSearchInput] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');

  useEffect(() => {
    const handle = setTimeout(() => setDebouncedSearch(searchInput.trim()), 300);
    return () => clearTimeout(handle);
  }, [searchInput]);

  const fetchAccounts = useCallback(async (query: string) => {
    setLoading(true);
    const path = query ? `/admin/account?q=${encodeURIComponent(query)}` : '/admin/account';
    const result: any = await apiRef.current.get(path);
    if (result?.error) {
      setError(result.message ?? 'Failed to load accounts');
    } else {
      setRows((result?.data ?? []) as AdminAccountRow[]);
      setError(null);
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    fetchAccounts(debouncedSearch);
  }, [fetchAccounts, debouncedSearch]);

  const handleStripeLinked = useCallback((accountId: number, customerId: string) => {
    setRows((prev) => prev.map((r) => (r.id === accountId ? { ...r, stripeCustomerId: customerId } : r)));
  }, []);

  const columns: GridColDef<AdminAccountRow>[] = useMemo(
    () => [
      { field: 'id', headerName: 'ID', width: 80, type: 'number' },
      { field: 'name', headerName: 'Account', flex: 1.2, minWidth: 180 },
      {
        field: 'createdAt',
        headerName: 'Created',
        width: 120,
        valueFormatter: (value) => formatDate(value),
      },
      {
        field: 'planSlug',
        headerName: 'Plan',
        width: 140,
        valueGetter: (_v, row) => row.subscription?.planSlug ?? '-',
      },
      {
        field: 'ownerEmail',
        headerName: 'Owner Email',
        flex: 1,
        minWidth: 200,
        valueGetter: (_v, row) => row.ownerEmail ?? '-',
      },
      {
        field: 'status',
        headerName: 'Status',
        width: 120,
        renderCell: ({ row }) => {
          const status = row.subscription?.status ?? 'none';
          return (
            <Chip size="small" label={status} color={status === 'active' ? 'success' : 'default'} variant="outlined" />
          );
        },
      },
      {
        field: 'stripe',
        headerName: 'Stripe',
        width: 110,
        sortable: false,
        filterable: false,
        renderCell: ({ row }) => {
          const url = stripeCustomerUrl(row.stripeCustomerId);
          if (!url) {
            return (
              <LinkStripeButton accountId={row.id} onLinked={(customerId) => handleStripeLinked(row.id, customerId)} />
            );
          }
          return (
            <Button
              size="small"
              endIcon={<OpenInNew sx={{ fontSize: 14 }} />}
              onClick={(event) => event.stopPropagation()}
              href={url}
              target="_blank"
              rel="noreferrer"
            >
              Open
            </Button>
          );
        },
      },
      {
        field: 'overrides',
        headerName: 'Overrides',
        width: 100,
        sortable: false,
        filterable: false,
        renderCell: ({ row }) => {
          const hasOverrides =
            row.subscription?.featureOverrides && Object.keys(row.subscription.featureOverrides).length > 0;
          return hasOverrides ? (
            <Chip size="small" label="Custom" color="warning" />
          ) : (
            <Typography variant="caption" color="text.disabled">
              —
            </Typography>
          );
        },
      },
      {
        field: 'actions',
        headerName: '',
        width: 60,
        sortable: false,
        filterable: false,
        renderCell: ({ row }) => (
          <IconButton
            size="small"
            disabled={!row.subscription}
            onClick={(event) => {
              event.stopPropagation();
              setOpenAccount(row);
            }}
            aria-label="Open account"
          >
            <Edit fontSize="small" />
          </IconButton>
        ),
      },
    ],
    [handleStripeLinked],
  );

  return (
    <Box>
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <Stack direction="row" spacing={2} alignItems="center" justifyContent="space-between" sx={{ mb: 2 }}>
        <Stack direction="row" spacing={1} alignItems="center">
          <TextField
            size="small"
            placeholder="Search by name, ID, or email"
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            sx={{ minWidth: 280 }}
          />
          <Button size="small" onClick={() => setSearchInput('')} disabled={searchInput === ''}>
            Clear
          </Button>
        </Stack>
      </Stack>

      <DataGridPro
        rows={rows}
        columns={columns}
        loading={loading}
        autoHeight
        pagination
        pageSizeOptions={[25, 50, 100]}
        initialState={{ pagination: { paginationModel: { pageSize: 25 } } }}
        onRowClick={(params) => setOpenAccount(params.row as AdminAccountRow)}
        sx={{ '& .MuiDataGrid-row': { cursor: 'pointer' } }}
      />

      {loading && <CircularProgress size={20} sx={{ mt: 2 }} />}

      <AdminAccountDetailPanel
        account={openAccount}
        onClose={() => setOpenAccount(null)}
        onSaved={async () => {
          setOpenAccount(null);
          await fetchAccounts(debouncedSearch);
        }}
      />
    </Box>
  );
}

// ── Link Stripe button (used in table Stripe column) ──────────────────────

function LinkStripeButton({
  accountId,
  onLinked,
}: {
  accountId: number;
  onLinked: (customerId: string) => void;
}): React.ReactElement {
  const api = useApi();
  const [linking, setLinking] = useState(false);

  const handleLink = async (): Promise<void> => {
    setLinking(true);
    const result: any = await api.post(`/admin/account/${accountId}/link-stripe`, {});
    setLinking(false);
    if (result?.data?.customerId) {
      onLinked(result.data.customerId);
    }
  };

  return (
    <Button
      size="small"
      onClick={(event) => {
        event.stopPropagation();
        void handleLink();
      }}
      disabled={linking}
      startIcon={linking ? <CircularProgress size={12} /> : undefined}
    >
      {linking ? 'Linking…' : 'Link'}
    </Button>
  );
}

export default AdminAccountTable;
